import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, ArrowRight } from "lucide-react";
import api from "../lib/api";
import SupportChat from "./SupportChat";

// Nav entry to the candidate's conversation with VetraHub. Mirrors the apply inbox peek: icon with
// an unread badge, dropdown for the conversation, full page behind it. In the GLOBAL nav rather than
// the apply hub because support questions are not only about applying — billing, résumés, accounts.
const POLL_MS = 60000;

export default function SupportPeek() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);

  const poll = useCallback(() => {
    api.get("/support/unread").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, POLL_MS);
    window.addEventListener("focus", poll);
    return () => { clearInterval(t); window.removeEventListener("focus", poll); };
  }, [poll]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); btnRef.current?.focus(); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button ref={btnRef} type="button" onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog" aria-expanded={open}
        aria-label={unread ? `Messages, ${unread} unread` : "Messages"}
        className="relative inline-flex items-center hover:text-[#1D4ED8] transition-colors"
        data-testid="nav-support">
        <MessageCircle className="w-[18px] h-[18px]" />
        {unread > 0 && (
          <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 bg-[#E11D48] text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div role="dialog" aria-label="Messages with VetraHub"
          className="absolute right-0 mt-3 w-[min(380px,calc(100vw-32px))] rounded-2xl border border-border bg-white shadow-xl z-50 overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-border">
            <div className="text-[13px] font-bold text-[#0F172A]">Messages</div>
            <p className="text-[11px] text-muted-foreground mt-0.5">Questions, or an application that won't go through.</p>
          </div>
          <SupportChat compact onUnread={setUnread} />
          <button type="button" onClick={() => { setOpen(false); navigate("/support"); }}
            className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-[12px] font-semibold text-[#0025D8] hover:bg-[#F8FAFC] border-t border-border transition-colors">
            Open full view <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
